import clsx from 'clsx';
import type { Message } from '@flock-types/chat.types';
import Avatar from '@/components/Avatar';
import MessageBubble from './MessageBubble';
import ReactionDisplay from './ReactionDisplay';

export interface MessageRowProps {
  message: Message;
  own: boolean;
  /** Suppress avatar + name when grouped under the previous message. */
  grouped: boolean;
  onReplyClick?: (messageId: string) => void;
  onToggleReaction?: (message: Message, emoji: string, reacted: boolean) => void;
  renderRichContent?: (message: Message, own: boolean) => React.ReactNode;
  /** Right-click or click on the message opens the actions menu (mobile parity). */
  onOpenActions?: (message: Message) => void;
  /** Fresh avatar URL resolver (stored message photo URLs expire). */
  resolveAvatar?: (message: Message) => string | null | undefined;
  /** Highlight (search jump target). */
  highlighted?: boolean;
}

export default function MessageRow({
  message,
  own,
  grouped,
  onReplyClick,
  onToggleReaction,
  renderRichContent,
  onOpenActions,
  resolveAvatar,
  highlighted,
}: MessageRowProps) {
  return (
    <div
      className={clsx(
        'relative flex gap-3 px-4',
        grouped ? 'mt-0.5' : 'mt-3',
        own && 'flex-row-reverse',
        highlighted && 'animate-pulse rounded-lg bg-flock-navy-100/70'
      )}
      data-message-id={message.id}
      onContextMenu={(e) => {
        if (!onOpenActions) return;
        // Text is selected — leave the native menu so Copy works
        if (!window.getSelection()?.isCollapsed) return;
        e.preventDefault();
        onOpenActions(message);
      }}
    >
      <div className="w-9 shrink-0">
        {!own && !grouped && (
          <Avatar
            src={resolveAvatar?.(message) ?? message.userProfileImageUrl}
            name={message.userName}
            sizeClass="h-9 w-9"
          />
        )}
      </div>

      <div className={clsx('min-w-0 max-w-[75%]', own && 'flex flex-col items-end')}>
        {!own && !grouped && (
          <div className="mb-0.5 px-1 text-xs font-semibold text-flock-navy-500">
            {message.userName}
          </div>
        )}
        <div
          onClick={(e) => {
            // Click on the bubble (not links/buttons/embeds inside it) opens actions
            if (!onOpenActions) return;
            const target = e.target as HTMLElement;
            if (target.closest('a, button, iframe')) return;
            // A drag-select ends in a click with a live selection — don't
            // hijack it (a plain click collapses the selection first)
            if (!window.getSelection()?.isCollapsed) return;
            onOpenActions(message);
          }}
        >
          <MessageBubble
            message={message}
            own={own}
            onReplyClick={onReplyClick}
            renderRichContent={renderRichContent}
          />
        </div>
        <ReactionDisplay
          message={message}
          own={own}
          onToggle={(emoji, reacted) => onToggleReaction?.(message, emoji, reacted)}
        />
      </div>
    </div>
  );
}
